/**
 * vue-i18n v11 removed the Legacy API `tc` / `$tc`
 * (https://vue-i18n.intlify.dev/guide/migration/breaking11).
 *
 * Frontdesk still runs in Legacy API mode and calls `tc` / `$tc` in many places,
 * also for plain interpolation, because `i18n.global.t` is wrapped in
 * `src/localize.ts` and only forwards the key.
 *
 * `createLegacyTc` rebuilds `tc` on top of the original `t`, with the same
 * argument handling as `VueI18n#tc` in vue-i18n 10.0.8, so existing call sites
 * keep their output. In v11 the legacy `t` passes every argument to the composer,
 * which supports the `plural` and `locale` options.
 *
 * One extra step keeps the v10 output: when `named` has a numeric `count` (or `n`),
 * it is passed as `plural`. vue-i18n 10 filled `{count}` / `{n}` from that value,
 * while vue-i18n 11 fills them from `plural` and turns `count: 0` into the
 * default `plural` of 1.
 */

type Translate = (...args: unknown[]) => unknown

export type LegacyTc = (key: string, ...args: unknown[]) => string

type TranslateOptions = {
  plural: number
  locale?: string
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Object.prototype.toString.call(value) === '[object Object]'

export function createLegacyTc (t: Translate): LegacyTc {
  return (key: string, ...args: unknown[]): string => {
    const [arg2, arg3] = args
    const options: TranslateOptions = { plural: 1 }
    let list: unknown[] | null = null
    let named: Record<string, unknown> | null = null

    if (typeof arg2 === 'string') {
      options.locale = arg2
    } else if (typeof arg2 === 'number') {
      options.plural = arg2
    } else if (Array.isArray(arg2)) {
      list = arg2
    } else if (isPlainObject(arg2)) {
      named = arg2
    }

    if (typeof arg3 === 'string') {
      options.locale = arg3
    } else if (Array.isArray(arg3)) {
      list = arg3
    } else if (isPlainObject(arg3)) {
      named = arg3
    }

    if (named && !list) {
      if (typeof named.count === 'number') {
        options.plural = named.count
      } else if (typeof named.n === 'number') {
        options.plural = named.n
      }
    }

    return t(key, list || named || {}, options) as string
  }
}
